import { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useGetAnnouncementDocumentsByIdQuery } from '../../../../../api';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../../api/endpoints/documentContent';
import DocumentDownloadService from '../../../../../utils/documentDownloadService';

const AnnouncementDetailDialog = ({ visible, onHide, announcement }) => {
  const { th } = useLanguage();
  const toastRef = useRef(null);
  const [downloadService] = useState(() => new DocumentDownloadService(toastRef));
  const [triggerDownloadByKey] = useLazyDownloadDocumentByKeyQuery();
  const [isDownloading, setIsDownloading] = useState(false);

  // Generate typeName if not present
  const getTypeName = (type) => {
    const typeMap = {
      128: th('announcement.typeLabels.sales'),
      129: th('announcement.typeLabels.marketing'),
      130: th('announcement.typeLabels.afterSales'),
      131: th('announcement.typeLabels.warranty'),
      132: th('announcement.typeLabels.spareParts')
    };
    return typeMap[type] || '-';
  };

  const displayTypeName = announcement?.typeName || getTypeName(announcement?.type);

  // Fetch documents
  const { data: documentsRaw, isFetching: docsFetching } = useGetAnnouncementDocumentsByIdQuery(
    announcement?.id,
    { skip: !announcement?.id || !visible }
  );

  const documents = Array.isArray(documentsRaw) 
    ? documentsRaw 
    : (documentsRaw?.data ?? documentsRaw?.items ?? documentsRaw?.Data ?? []);
  

  const getFileKey = (d) =>
    d?.fileKey ?? d?.FileKey ?? d?.key ?? d?.Key ?? d?.documentKey ?? d?.DocumentKey ?? d?.storageKey ?? d?.StorageKey ?? null;

  const handleDownload = async () => {

    if (!documents || documents.length === 0) {
      toastRef.current?.show({
        severity: 'warn',
        detail: th('announcement.noDocumentFoundForDownload'),
        life: 2500
      });
      return;
    }

    const doc = documents.find((x) => !!getFileKey(x)) ?? documents[0];
    
    if (!doc) {
      toastRef.current?.show({
        severity: 'warn',
        detail: th('announcement.noDocumentFoundForDownload'),
        life: 2500
      });
      return;
    }

    const key = getFileKey(doc);
    
    if (!key) {
      toastRef.current?.show({
        severity: 'warn',
        detail: th('announcement.fileKeyErrorMessage'),
        life: 2500
      });
      return;
    }

    try {
      setIsDownloading(true);
      await downloadService.downloadByKey(triggerDownloadByKey, key, doc, {
        successMessage: th('announcement.downloadDocumentSuccessMessage'),
        errorMessage: th('announcement.downloadDocumentErrorMessage')
      });
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        detail: th('announcement.downloadDocumentErrorMessage'),
        life: 3000
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR');
  };

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        visible={visible}
        onHide={onHide}
        header={th('announcement.detailTitle')}
        style={{ width: '50vw', maxWidth: '700px' }}
        modal
        className="p-fluid"
      >
        {/* Info message if limited data */}
        {announcement && Object.keys(announcement).length <= 3 && (
          <div className="col-12 mb-3">
            <div className="p-3 bg-blue-50 border-round">
              <i className="pi pi-info-circle mr-2 text-blue-500"></i>
              <span className="text-blue-700">
                Detaylı bilgi backend&apos;den alınamadı. Sadece özet bilgiler gösteriliyor.
              </span>
            </div>
          </div>
        )}

        <div className="grid">
          {/* Duyuru Tipi */}
          <div className="col-12">
            <div className="field mb-3">
              <label className="font-bold block mb-2">{th('announcement.columnHeaders.type')}</label>
              <div className="text-900">{displayTypeName}</div>
            </div>
          </div>

          {/* Başlık */}
          <div className="col-12">
            <div className="field mb-3">
              <label className="font-bold block mb-2">{th('announcement.columnHeaders.subject')}</label>
              <div className="text-900">{announcement?.announcementSubject || '-'}</div>
            </div>
          </div>

          {/* Duyuru Tarihi */}
          <div className="col-12">
            <div className="field mb-3">
              <label className="font-bold block mb-2">{th('announcement.columnHeaders.announcementDate')}</label>
              <div className="text-900">{formatDate(announcement?.announcementDate)}</div>
            </div>
          </div>

          {/* Duyuru İçeriği */}
          <div className="col-12">
            <div className="field mb-3">
              <label className="font-bold block mb-2">{th('announcement.columnHeaders.content')}</label>
              <div className="text-900" style={{ whiteSpace: 'pre-wrap' }}>
                {announcement?.announcementContent || '-'}
              </div>
            </div>
          </div>

          {/* Duyuruyu Yayınlayan */}
          <div className="col-12">
            <div className="field mb-3">
              <label className="font-bold block mb-2">{th('announcement.formLabels.regionalManager')}</label>
              <div className="text-900">{announcement?.managerUserName || '-'}</div>
            </div>
          </div>

          {/* Ek Doküman */}
          <div className="col-12">
            <div className="field mb-0">
              <label className="font-bold block mb-2">{th('announcement.formLabels.fileUpload')}</label>
              <Button
                label={th('common.download')}
                icon={isDownloading ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
                onClick={handleDownload}
                disabled={isDownloading || docsFetching || !documents || documents.length === 0}
                className="p-button-outlined"
              />
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AnnouncementDetailDialog;
